import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack, Save } from '@mui/icons-material';
import FacebookIcon from '@mui/icons-material/Facebook';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../../api/client';

const FACEBOOK_ORIGINS = new Set(['https://www.facebook.com', 'https://web.facebook.com']);

const FacebookDetailsPage = ({ session = {} }) => {
  const navigate = useNavigate();

  const initialForm = useMemo(
    () => ({
      fb_number: '',
      fb_phonenumberid: '',
      fb_businessaccid: '',
      fb_appid: '',
      fb_apiversion: 'v21.0',
      fb_accesstoken: '',
      fb_catalog_id: '',
    }),
    []
  );

  const [formData, setFormData] = useState(initialForm);
  const [status, setStatus] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connected, setConnected] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);
  const [sdkError, setSdkError] = useState('');
  const [signupProcessing, setSignupProcessing] = useState(false);
  const [pendingSignup, setPendingSignup] = useState(null);
  const [authCode, setAuthCode] = useState('');
  const [savedFbId, setSavedFbId] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [businessStatus, setBusinessStatus] = useState({ state: 'idle', message: '' });
  const [modalState, setModalState] = useState({
    open: false,
    status: 'idle',
    title: '',
    message: '',
  });
  const sessionUid = session?.uid || session?.u_id || session?.uId || '';
  const fbAppId = process.env.REACT_APP_FB_APP_ID || '';
  const fbConfigId = process.env.REACT_APP_FB_CONFIG_ID || '';
  const fbApiVersion = 'v21.0';
  const inputSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#ffffff',
      fontSize: '0.9rem',
      '& fieldset': { borderColor: '#d9d9d9' },
      '&:hover fieldset': { borderColor: 'primary.main' },
      '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: 1.5 },
    },
    '& .MuiInputBase-input': { padding: '10px 12px' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const loadDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/facebook/details');
      const details = response?.data?.data || {};
      setFormData((prev) => ({
        ...prev,
        fb_number: details.fb_number || '',
        fb_phonenumberid: details.fb_phonenumberid || '',
        fb_businessaccid: details.fb_businessaccid || '',
        fb_appid: details.fb_appid || '',
        fb_apiversion: details.fb_apiversion || prev.fb_apiversion,
        fb_accesstoken: details.fb_accesstoken || '',
        fb_catalog_id: details.fb_catalog_id || '',
      }));
      setConnected(true);
      setStatus({ type: '', text: '' });
    } catch (error) {
      if (error?.response?.status === 404) {
        setConnected(false);
        setFormData(initialForm);
        setStatus({ type: '', text: '' });
      } else {
        const msg = error?.response?.data?.error || 'Failed to load Facebook details';
        setStatus({ type: 'error', text: msg });
      }
    } finally {
      setLoading(false);
    }
  }, [initialForm]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  const openModal = useCallback((statusType, title, message) => {
    setModalState({ open: true, status: statusType, title, message });
  }, []);

  const closeModal = useCallback(() => {
    setModalState((prev) => (prev.status === 'loading' ? prev : { ...prev, open: false }));
  }, []);

  const fetchBusinesses = useCallback(
    async (accessToken) => {
      setBusinessStatus({ state: 'loading', message: 'Loading business accounts...' });
      try {
        const response = await axios.get(`https://graph.facebook.com/${fbApiVersion}/me/businesses`, {
          params: { access_token: accessToken },
        });
        const list = response?.data?.data || [];
        setBusinesses(list);
        if (list.length === 0) {
          setBusinessStatus({ state: 'empty', message: 'No businesses found.' });
        } else {
          setBusinessStatus({ state: 'success', message: 'Business accounts loaded.' });
        }
        return { ok: true, empty: list.length === 0, data: list };
      } catch (error) {
        const message =
          error?.response?.data?.error?.message || 'Unable to load business accounts.';
        setBusinesses([]);
        setBusinessStatus({ state: 'error', message });
        return { ok: false, error: message };
      }
    },
    [fbApiVersion]
  );

  const finalizeSignup = useCallback(
    async (details, code) => {
      if (!sessionUid) {
        openModal('error', 'User not available', 'Please sign in again and retry the signup.');
        return;
      }

      setSignupProcessing(true);
      setPendingSignup(null);

      try {
        openModal('loading', 'Saving Facebook details', 'Storing your WhatsApp business details.');
        const savePayload = {
          uid: sessionUid,
          fb_businessid: details.businessId,
          fb_businessaccid: details.wabaId,
          fb_appid: fbAppId,
          fb_phonenumberid: details.phoneNumberId,
          fb_apiversion: fbApiVersion,
        };
        if (details.phoneNumber) {
          savePayload.fb_number = String(details.phoneNumber);
        }

        const saveResponse = await apiClient.post('/save/fbdetails', savePayload);
        const fbId =
          saveResponse?.data?.data?.fb_id ||
          saveResponse?.data?.data?.insertId ||
          saveResponse?.data?.fb_id;

        if (!fbId) {
          throw new Error('Facebook details could not be saved.');
        }

        setSavedFbId(fbId);

        openModal('loading', 'Exchanging code', 'Requesting a Meta access token.');
        const tokenResponse = await apiClient.post('/facebook/token', {
          code,
          fb_apiversion: fbApiVersion,
        });
        const accessToken = tokenResponse?.data?.data?.access_token;
        if (!accessToken) {
          throw new Error('Access token was not returned by the server.');
        }

        openModal('loading', 'Updating access token', 'Finalizing your connection.');
        await apiClient.post('/update/fbdetails', {
          uid: sessionUid,
          id: fbId,
          fb_accesstoken: accessToken,
        });

        openModal('loading', 'Fetching businesses', 'Loading your business accounts.');
        const businessResult = await fetchBusinesses(accessToken);

        if (!businessResult.ok) {
          openModal('error', 'Connected with warnings', businessResult.error);
        } else if (businessResult.empty) {
          openModal('success', 'Connected', 'No business accounts were found.');
        } else {
          openModal('success', 'Connected', 'WhatsApp embedded signup completed successfully.');
        }

        setConnected(true);
        await loadDetails();
      } catch (error) {
        const message =
          error?.response?.data?.error ||
          error?.message ||
          'Signup failed. Please try again.';
        openModal('error', 'Signup failed', message);
      } finally {
        setSignupProcessing(false);
      }
    },
    [fbAppId, fetchBusinesses, fbApiVersion, loadDetails, openModal, sessionUid]
  );

  useEffect(() => {
    if (!fbAppId) {
      setSdkError('Facebook App ID is not configured.');
      return;
    }

    if (window.FB) {
      window.FB.init({
        appId: fbAppId,
        cookie: true,
        xfbml: false,
        version: fbApiVersion,
      });
      setSdkReady(true);
      setSdkError('');
      return;
    }

    window.fbAsyncInit = function fbInit() {
      window.FB.init({
        appId: fbAppId,
        cookie: true,
        xfbml: false,
        version: fbApiVersion,
      });
      setSdkReady(true);
      setSdkError('');
    };

    const existingScript = document.getElementById('facebook-jssdk');
    if (existingScript) return;

    const script = document.createElement('script');
    script.id = 'facebook-jssdk';
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.defer = true;
    script.onerror = () => setSdkError('Unable to load Facebook SDK.');
    document.body.appendChild(script);
  }, [fbAppId, fbApiVersion]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (!FACEBOOK_ORIGINS.has(event.origin)) return;

      let payload = event.data;
      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload);
        } catch (_) {
          return;
        }
      }
      if (!payload || payload.type !== 'WA_EMBEDDED_SIGNUP') return;

      const eventName = payload.event || payload.event_name || payload?.data?.event || '';
      if (!eventName) return;

      if (eventName === 'FINISH') {
        const data = payload.data || payload;
        const details = {
          businessId: data.business_id || payload.business_id || '',
          wabaId: data.waba_id || payload.waba_id || '',
          phoneNumberId: data.phone_number_id || payload.phone_number_id || '',
          phoneNumber: data.phone_number || data.display_phone_number || '',
        };

        if (!details.businessId || !details.wabaId || !details.phoneNumberId) {
          openModal('error', 'Signup incomplete', 'Missing business details from Facebook.');
          return;
        }

        if (authCode) {
          finalizeSignup(details, authCode);
        } else {
          setPendingSignup(details);
          openModal('loading', 'Waiting for login', 'Complete Facebook login to continue.');
        }
        return;
      }

      if (eventName === 'CANCEL') {
        openModal('error', 'Signup canceled', 'Facebook signup was canceled.');
        setPendingSignup(null);
        return;
      }

      if (eventName === 'ERROR') {
        const message =
          payload?.data?.error_message ||
          payload?.error_message ||
          'Facebook signup failed.';
        openModal('error', 'Signup error', message);
        setPendingSignup(null);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [authCode, finalizeSignup, openModal]);

  useEffect(() => {
    if (pendingSignup && authCode && !signupProcessing) {
      finalizeSignup(pendingSignup, authCode);
    }
  }, [authCode, finalizeSignup, pendingSignup, signupProcessing]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      fb_number: formData.fb_number.trim(),
      fb_phonenumberid: formData.fb_phonenumberid.trim(),
      fb_businessaccid: formData.fb_businessaccid.trim(),
      fb_appid: formData.fb_appid.trim(),
      fb_apiversion: formData.fb_apiversion.trim(),
      fb_catalog_id: formData.fb_catalog_id.trim(),
    };
    if (formData.fb_accesstoken.trim()) {
      payload.fb_accesstoken = formData.fb_accesstoken.trim();
    }

    try {
      const request = connected
        ? apiClient.put('/facebook/details', payload)
        : apiClient.post('/facebook/connect', payload);
      const response = await request;
      setStatus({
        type: 'success',
        text: response?.data?.message || 'Facebook details saved.',
      });
      setConnected(true);
      if (!connected) {
        await loadDetails();
      }
    } catch (error) {
      const msg = error?.response?.data?.error || 'Unable to save Facebook details';
      setStatus({ type: 'error', text: msg });
    } finally {
      setSaving(false);
    }
  };

  const handleStartSignup = () => {
    if (!sessionUid) {
      openModal('error', 'User not available', 'Please sign in again and retry the signup.');
      return;
    }
    if (!sdkReady) {
      openModal('error', 'Facebook SDK not ready', 'Please wait for the SDK to finish loading.');
      return;
    }
    if (!fbAppId || !fbConfigId) {
      openModal(
        'error',
        'Missing Facebook config',
        'REACT_APP_FB_APP_ID or REACT_APP_FB_CONFIG_ID is not configured.'
      );
      return;
    }

    setAuthCode('');
    setPendingSignup(null);
    setSavedFbId(null);
    setBusinesses([]);
    setBusinessStatus({ state: 'idle', message: '' });

    openModal(
      'loading',
      'Opening Facebook login',
      'Complete login and embedded signup in the Facebook popup.'
    );

    window.FB.login(
      (response) => {
        const code = response?.authResponse?.code || response?.code;
        if (!code) {
          const message =
            response?.status === 'not_authorized'
              ? 'Facebook authorization was not granted.'
              : 'Facebook login was canceled or failed.';
          openModal('error', 'Login failed', message);
          return;
        }

        setAuthCode(code);
        openModal('loading', 'Waiting for signup', 'Finish WhatsApp embedded signup to continue.');
      },
      {
        config_id: fbConfigId,
        response_type: 'code',
        override_default_response_type: true,
      }
    );
  };

  const isBusy = saving || signupProcessing;
  const canStartSignup = sdkReady && !sdkError && fbAppId && fbConfigId;
  const signupDisabled = isBusy || !canStartSignup;
  const sdkStatusLabel = sdkError ? 'SDK Error' : sdkReady ? 'SDK Ready' : 'SDK Loading';
  const sdkStatusColor = sdkError ? 'error' : sdkReady ? 'success' : 'default';

  const statusColor = status.type === 'success' ? 'success' : status.type === 'error' ? 'error' : 'info';
  const renderStatus = status.text ? (
    <Alert severity={statusColor}>{status.text}</Alert>
  ) : null;

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '50vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Dialog open={modalState.open} onClose={closeModal} maxWidth="xs" fullWidth>
        <DialogTitle>{modalState.title || 'Status'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} alignItems="center" sx={{ py: 1 }}>
            {modalState.status === 'loading' && <CircularProgress />}
            <Typography variant="body2" color="text.secondary" align="center">
              {modalState.message || 'Processing your request.'}
            </Typography>
          </Stack>
        </DialogContent>
        {modalState.status !== 'loading' && (
          <DialogActions>
            <Button onClick={closeModal}>Close</Button>
          </DialogActions>
        )}
      </Dialog>

      <Box
        sx={{
          minHeight: 'calc(100vh - 64px)',
          background: 'linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)',
          display: 'flex',
          justifyContent: 'center',
          p: { xs: 2, md: 3 },
          pb: { xs: 14, md: 16 },
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 1100 }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <Button
              variant="text"
              startIcon={<ArrowBack fontSize="small" />}
              onClick={() => navigate(-1)}
              sx={{
                color: 'text.secondary',
                px: 0,
                minWidth: 0,
                fontWeight: 600,
              }}
            >
              Back
            </Button>
          </Stack>

        <Card
          sx={{
            borderRadius: 3,
            boxShadow: '0 18px 40px rgba(15, 23, 42, 0.08)',
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            mb: 3,
            background: (theme) =>
              `linear-gradient(120deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
            color: 'primary.contrastText',
          }}
        >
          <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <FacebookIcon />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" fontWeight={800}>
                  Facebook Details
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  Keep your Meta WhatsApp Business credentials updated for seamless messaging.
                </Typography>
              </Box>
              <Chip
                label={connected ? 'Connected' : 'Not Connected'}
                color={connected ? 'success' : 'default'}
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'inherit' }}
              />
            </Stack>
          </CardContent>
        </Card>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Stack spacing={2}>
              {!connected && (
                <Card
                  sx={{
                    borderRadius: 2.5,
                    boxShadow: '0 16px 36px rgba(15, 23, 42, 0.08)',
                    border: '1px solid',
                    borderColor: 'divider',
                    background: '#ffffff',
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Stack spacing={2}>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        justifyContent="space-between"
                      >
                        <Box>
                          <Typography variant="h6" fontWeight={700}>
                            WhatsApp Embedded Signup
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Connect your WhatsApp Business account with Facebook login.
                          </Typography>
                        </Box>
                        <Chip label={sdkStatusLabel} color={sdkStatusColor} />
                      </Stack>

                      {sdkError && <Alert severity="error">{sdkError}</Alert>}
                      {!fbAppId && (
                        <Alert severity="warning">
                          REACT_APP_FB_APP_ID is not configured.
                        </Alert>
                      )}
                      {!fbConfigId && (
                        <Alert severity="warning">
                          REACT_APP_FB_CONFIG_ID is not configured.
                        </Alert>
                      )}

                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                      >
                        <Button
                          variant="contained"
                          onClick={handleStartSignup}
                          disabled={signupDisabled}
                          sx={{ textTransform: 'none', px: 3 }}
                        >
                          Start Embedded Signup
                        </Button>
                        <Typography variant="body2" color="text.secondary">
                          Uses Meta embedded signup to link your business.
                        </Typography>
                      </Stack>

                      {savedFbId && (
                        <Alert severity="success">
                          Facebook details saved (ID: {savedFbId}).
                        </Alert>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              )}
            <Card
              sx={{
                borderRadius: 2.5,
                boxShadow: '0 16px 36px rgba(15, 23, 42, 0.08)',
                border: '1px solid',
                borderColor: 'divider',
                background: '#ffffff',
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Stack spacing={2} mb={2}>
                  {renderStatus}
                </Stack>

                <Box component="form" onSubmit={handleSubmit}>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Whatsapp Number"
                        name="fb_number"
                        value={formData.fb_number}
                        onChange={handleChange}
                        placeholder="Enter mobile number"
                        fullWidth
                        required
                        sx={inputSx}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Whatsapp Phonenumber Id"
                        name="fb_phonenumberid"
                        value={formData.fb_phonenumberid}
                        onChange={handleChange}
                        placeholder="Enter phone number ID"
                        fullWidth
                        required
                        sx={inputSx}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Whatsapp Business Id"
                        name="fb_businessaccid"
                        value={formData.fb_businessaccid}
                        onChange={handleChange}
                        placeholder="Enter WhatsApp Business ID"
                        fullWidth
                        required
                        sx={inputSx}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Facebook Business Id"
                        name="fb_appid"
                        value={formData.fb_appid}
                        onChange={handleChange}
                        placeholder="Enter Facebook Business ID"
                        fullWidth
                        required
                        sx={inputSx}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="API Version"
                        name="fb_apiversion"
                        value={formData.fb_apiversion}
                        onChange={handleChange}
                        placeholder="Enter API version"
                        fullWidth
                        required
                        sx={inputSx}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Access Token"
                        name="fb_accesstoken"
                        value={formData.fb_accesstoken}
                        onChange={handleChange}
                        placeholder="Enter access token"
                        fullWidth
                        sx={inputSx}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Stack direction="row" justifyContent="flex-end">
                        <Button
                          type="submit"
                          variant="contained"
                          startIcon={<Save />}
                          disabled={isBusy}
                          sx={{
                            textTransform: 'none',
                            px: 3.5,
                            fontSize: '0.95rem',
                            py: 1.1,
                            borderRadius: 2,
                            boxShadow: '0 10px 20px rgba(15, 23, 42, 0.18)',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              boxShadow: '0 12px 22px rgba(15, 23, 42, 0.22)',
                              transform: 'translateY(-1px)',
                            },
                            '&:active': {
                              transform: 'translateY(0)',
                              boxShadow: '0 8px 16px rgba(15, 23, 42, 0.18)',
                            },
                            '& .MuiButton-startIcon': { marginRight: 1 },
                          }}
                        >
                          {saving ? 'Saving...' : 'Update'}
                        </Button>
                      </Stack>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack spacing={2}>
              <Card
                sx={{
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  background: '#ffffff',
                }}
              >
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    Integration Checklist
                  </Typography>
                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      • Verify WhatsApp number is active
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Ensure Business IDs match Meta account
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Confirm API version is supported
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
              <Card
                sx={{
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  background: '#ffffff',
                }}
              >
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    Status
                  </Typography>
                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      Connection: {connected ? 'Active' : 'Not connected'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      API Version: {formData.fb_apiversion || 'v21.0'}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
              <Card
                sx={{
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  background: '#ffffff',
                }}
              >
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    Business Accounts
                  </Typography>
                  <Stack spacing={1.5}>
                    {businessStatus.state === 'loading' && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="text.secondary">
                          {businessStatus.message || 'Loading...'}
                        </Typography>
                      </Stack>
                    )}
                    {businessStatus.state === 'error' && (
                      <Alert severity="error">{businessStatus.message}</Alert>
                    )}
                    {businessStatus.state === 'empty' && (
                      <Typography variant="body2" color="text.secondary">
                        {businessStatus.message || 'No businesses found.'}
                      </Typography>
                    )}
                    {businessStatus.state === 'success' && (
                      <Stack spacing={1}>
                        {businesses.map((item, index) => (
                          <Box
                            key={item.id || item.name || index}
                            sx={{ p: 1, borderRadius: 1, bgcolor: '#f8fafc' }}
                          >
                            <Typography variant="body2" fontWeight={600}>
                              {item.name || 'Business'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {item.id || 'N/A'}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    )}
                    {businessStatus.state === 'idle' && (
                      <Typography variant="body2" color="text.secondary">
                        Connect to load business accounts.
                      </Typography>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </Grid>
        </Grid>
        </Box>
      </Box>
    </>
  );
};

export default FacebookDetailsPage;
